import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { arenaToast } from 'src/components/arenaSnackbar';
import {
  aiInviteErrorMessage,
  joinErrorMessage,
  nudgeErrorMessage,
  ROUND_STARTING_GAVE_UP_MESSAGE,
  ROUND_STARTING_MESSAGE,
  ROUND_STARTING_RETRY,
  ROUND_STARTING_RETRY_ATTEMPTS,
  ROUND_STARTING_RETRY_DELAY_MS,
  startErrorMessage,
} from 'src/pages/skillBuilder/constants/multiplayer';
import type { IntroContent } from 'src/redux/types/skillBuilder';
import type { AiInviteResponse } from 'src/pages/skillBuilder/types/Multiplayer';
import type { CourseFeedbackScope } from 'src/pages/course/types';
import type {
  AIAssistantFeedbackBody,
  AttemptQuestionBody,
  ChatMessageBody,
  HostTransferBody,
  MultiplayerGameInitializeBody,
  MultiplayerPromptUpdateBody,
  MultiplayerQuestionsGenerationBody,
  MultiplayerRemoveOrBlockUserBody,
  MultiplayerRoomNameUpdateBody,
  QuestionAIAssistantBody,
  SkillBuilderMultiplayerHostSessionParam,
  SkillBuilderMultiplayerJoinSessionParam,
  TransferRoomAdminBody,
  UnblockMultiplayerUserBody,
  MultiplayerReactionBodyReactionEnum,
} from 'src/openapi';
import { getApiErrorMessage, getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';

export const getUnansweredQuestionBySessionId = createAsyncThunk(
  'get/unanswered-question-by-skillIds',
  async (
    {
      session_id,
    }: {
      session_id: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetUnansweredQuestionSkillBuilder({ sessionId: session_id });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchLeaderboardData = createAsyncThunk(
  'get/leaderboard-data',
  async (
    {
      competitionId,
      onlyFollowed,
      page,
      pageSize,
      skillId,
      timePeriod,
      userSearchQuery,
      userType,
    }: {
      competitionId?: string;
      onlyFollowed?: boolean;
      page?: number;
      pageSize?: number;
      skillId?: string;
      timePeriod?: string;
      userSearchQuery?: string;
      userType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetLeaderboardByUser({
        competitionId,
        onlyFollowed,
        page,
        pageSize,
        skillId,
        timePeriod,
        userSearchQuery,
        userType,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getQuestionAIAssistantChats = createAsyncThunk(
  'question/get/ai-assistant-chats',
  async (
    { questionId }: { questionId: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      ).questionApiGetQuestionAiAssistantChats({ questionId }, { signal });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const questionAIAssistantQuery = createAsyncThunk(
  'question/post/ai-assistant-query',
  async (
    {
      questionId,
      payload,
    }: {
      questionId: string;
      payload: QuestionAIAssistantBody & { parent_response_id?: string };
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      )
        // parent_response_id (conversation memory) isn't in the generated spec
        // yet — cast until `npm run update-spec`.
        .questionApiQuestionAiAssistant({
          questionId,
          questionAIAssistantBody: payload,
        });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

// Canonical cached "why" for a question, fetched the moment an answer is
// submitted. Returns status "ready" | "generating" | "failed"; on a cache miss
// the socket event `question_explanation_ready` delivers the result.
export const getQuestionExplanation = createAsyncThunk(
  'question/get/explanation',
  async ({ questionId }: { questionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetQuestionExplanation({ questionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchAttemptedQuestion = createAsyncThunk(
  'get/attempted-question',
  async ({ attemptId }: { attemptId: string }, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      ).questionApiGetAnsweredAttemptById({ attemptId }, { signal });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const unlockAIGuide = createAsyncThunk(
  'put/skill-builder/unlock-ai-guide',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiSkillAiAssistantUnlock({ skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const userQuestionFeedback = createAsyncThunk(
  'put/question-user-feedback',
  async (
    {
      questionId,
      feedback,
      feedbackTypes,
    }: { questionId: string; feedback?: string; feedbackTypes?: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiUpsertUserQuestionFeedback({
        questionId,
        userQuestionFeedbackParam: {
          user_custom_feedback: feedback,
          feedback_list: feedbackTypes,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const aiGuideFeedback = createAsyncThunk(
  'post/ai-guide-feedback',
  async (
    {
      chatId,
      questionId,
      aIAssistantFeedbackBody,
    }: {
      chatId: string;
      questionId: string;
      aIAssistantFeedbackBody: AIAssistantFeedbackBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getQuestionApi()
      ).questionApiAiAssistantFeedback({
        chatId,
        questionId,
        aIAssistantFeedbackBody,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getUserSkillDetails = createAsyncThunk(
  'GET/skill/<skill_id>/user/<user_id>',
  async (
    { skillId, userId }: { userId: string; skillId: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetUserSkillDetails({ skillId, userId }, { signal });
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const hostMultiplayerSession = createAsyncThunk(
  'skill-builder/host-multiplayer-session',
  async (
    {
      params,
    }: {
      params: SkillBuilderMultiplayerHostSessionParam;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSkillBuilderMultiplayerHost({
        skillBuilderMultiplayerHostSessionParam: params,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const joinMultiplayerSession = createAsyncThunk(
  'skill-builder/join-multiplayer-session',
  async (
    {
      params,
      silent,
    }: {
      params: SkillBuilderMultiplayerJoinSessionParam;
      silent?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSkillBuilderMultiplayerJoin({
        skillBuilderMultiplayerJoinSessionParam: params,
      });
      return response.data;
    } catch (err: any) {
      if (!silent)
        arenaToast(joinErrorMessage(err?.response?.data?.error), {
          tone: 'warning',
        });
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerRoomDetails = createAsyncThunk(
  'skill-builder/get-room-details',
  async (
    {
      roomId,
      activeGameUsers,
      // Someone opening the room, which is what lets the server put them back
      // into one they had left. Every other call here is a refetch and must
      // leave it unset, or a socket event arriving after a leave undoes it.
      entering,
    }: { roomId: string; activeGameUsers?: boolean; entering?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetMultiplayerRoomInfo({
        roomId,
        activeGameUsers,
        entering,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const startMultiplayerSession = createAsyncThunk(
  'skill-builder/start-multiplayer-session',
  async (
    {
      gameId,
      roomId,
    }: {
      gameId: string;
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSkillBuilderMultiplayerStartSession({ gameId, roomId });
      // A refusal the server answers 200 rather than 4xx --
      // generate_questions_first, reachable when the availability check passes
      // and this call then loses the race. Said here beside the rejected case,
      // because the thunk is the one place that already owns naming a refusal;
      // the fulfilled path used to return it silently and the host was left
      // pressing Start against an arena that did not move.
      if (
        response.data &&
        (response.data as { success?: boolean }).success === false
      ) {
        arenaToast(startErrorMessage((response.data as { msg?: string }).msg), {
          tone: 'warning',
        });
      }
      return response.data;
    } catch (err: any) {
      arenaToast(startErrorMessage(err?.response?.data?.error), {
        tone: 'warning',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

/**
 * Start a Challenge round against an AI persona.
 *
 * Its own endpoint rather than a flag on `startMultiplayerSession`: seating the
 * opponent has to happen before the ready-player gate is evaluated, and the
 * server picks the character and models how it plays. The response carries the
 * personas now in the room so the arena can draw the opponent card without a
 * second round-trip.
 */
export const startMultiplayerSessionWithPersona = createAsyncThunk(
  'skill-builder/start-multiplayer-session-with-persona',
  async (
    {
      gameId,
      roomId,
    }: {
      gameId: string;
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSkillBuilderMultiplayerStartWithPersona({ gameId, roomId });
      // See startMultiplayerSession: a 200 can still be a refusal.
      if (
        response.data &&
        (response.data as { success?: boolean }).success === false
      ) {
        arenaToast(startErrorMessage((response.data as { msg?: string }).msg), {
          tone: 'warning',
        });
      }
      return response.data;
    } catch (err: any) {
      arenaToast(startErrorMessage(err?.response?.data?.error), {
        tone: 'warning',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getMultiplayerStartStatistics = createAsyncThunk(
  'skill-builder/get-multiplayer-start-statistics',
  async (
    {
      roomId,
    }: {
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSkillBuilderMultiplayerSessionStartStatistics({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const attemptMultiplayerQuestion = createAsyncThunk(
  'skill-builder/attempt-multiplayer-question',
  async (
    {
      questionId,
      roomId,
      attemptData,
    }: {
      questionId: string;
      roomId: string;
      attemptData: AttemptQuestionBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiAttemptMultiplayerQuestion({
        questionId,
        roomId,
        attemptQuestionBody: attemptData,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerProgress = createAsyncThunk(
  'skill-builder/get-multiplayer-progress',
  async (
    {
      roomId,
      sessionId,
    }: {
      roomId: string;
      sessionId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSkillBuilderMultiplayerSessionProgress({
        roomId,
        sessionId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerSessionHistory = createAsyncThunk(
  'skill-builder/get-multiplayer-session-history',
  async (
    { gameId, roomId }: { gameId: string; roomId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetMultiplayerSessionHistory({ gameId, roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerLeaderboard = createAsyncThunk(
  'skill-builder/get-multiplayer-leaderboard',
  async (
    {
      roomId,
      gameId,
      sessionId,
      resultType = 'session',
    }: {
      roomId: string;
      gameId?: string;
      sessionId?: string;
      resultType?: 'session' | 'game' | 'overall';
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiMultiplayerLeaderboard({
        roomId,
        gameId,
        resultType,
        sessionId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendMultiplayerChat = createAsyncThunk(
  'skill-builder/send-multiplayer-chat',
  async (chatMessageBody: ChatMessageBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendChatMessage({ chatMessageBody });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerRoomChats = createAsyncThunk(
  'skill-builder/get-multiplayer-room-chats',
  async (
    {
      roomId,
      gameId,
      cursor,
      pageSize,
    }: {
      roomId: string;
      gameId?: string;
      cursor?: string;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatMessage({
        cursor,
        gameId,
        pageSize,
        roomId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateMultiplayerSkill = createAsyncThunk(
  'skill-builder/update-multiplayer-skill',
  async (
    {
      skillId,
      roomId,
    }: {
      roomId: string;
      skillId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiUpdateMultiplayerSkill({ roomId, skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

/**
 * Push a room invite to people who aren't in the room yet.
 *
 * Distinct from `nudgeMultiplayerUser`, which chases a participant who hasn't
 * marked ready. The server throttles per target per minute and reports which
 * ids it swallowed, so the caller can leave those buttons alone instead of
 * flipping them back and inviting twice.
 */
export const inviteToMultiplayerRoom = createAsyncThunk(
  'skill-builder/invite-to-multiplayer-room',
  async (
    { roomId, userIds }: { roomId: string; userIds: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiInviteToMultiplayerRoom({
        roomId,
        multiplayerInviteUsersBody: { user_ids: userIds },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

/**
 * Arm the server's reach-out to recently active players who are offline now.
 * Idempotent per room, and the server decides later whether to actually send,
 * so a failure here is never worth telling the host about.
 */
export const scheduleMultiplayerOfflineInvites = createAsyncThunk(
  'skill-builder/schedule-multiplayer-offline-invites',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiScheduleMultiplayerOfflineInvites({ roomId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const recordMultiplayerInviteClick = createAsyncThunk(
  'skill-builder/record-multiplayer-invite-click',
  async ({ inviteId }: { inviteId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiRecordMultiplayerInviteClick({ inviteId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * Stop looking for a player on this skill and look for one on any skill the
 * host has. The round is then played on a skill whoever answers also has —
 * picked by the server when it builds the round, since the opponent does not
 * exist yet.
 */
export const widenMultiplayerSkillSearch = createAsyncThunk(
  'skill-builder/widen-multiplayer-skill-search',
  async (
    { roomId, gameId }: { roomId: string; gameId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiWidenMultiplayerSkillSearch({ roomId, gameId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getMultiplayerStats = createAsyncThunk(
  'skill-builder/multiplayer-stats',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetMultiplayerStats({ signal });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);

export const muteMultiplayerInviter = createAsyncThunk(
  'skill-builder/mute-multiplayer-inviter',
  async ({ fromUserId }: { fromUserId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiMuteMultiplayerInviter({
        multiplayerMuteInviterBody: { from_user_id: fromUserId },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

/** Join an AI player's game: a room of your own, or a seat in one already running. */
export const acceptAiMultiplayerInvite = createAsyncThunk(
  'skill-builder/accept-ai-multiplayer-invite',
  async ({ batchId }: { batchId: string }, { rejectWithValue }) => {
    try {
      return await apiClient.acceptAiMultiplayerInvite(batchId);
    } catch (err: any) {
      arenaToast(aiInviteErrorMessage(err?.response?.data?.error), {
        tone: 'warning',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * Every answer to an AI invite short of joining. Silent on failure: it only
 * tunes how often and on what this person is invited, so a lost one costs an
 * invite somewhat too soon, never anything they would notice on screen.
 */
export const respondAiMultiplayerInvite = createAsyncThunk(
  'skill-builder/respond-ai-multiplayer-invite',
  async (
    { batchId, outcome }: { batchId: string; outcome: AiInviteResponse },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.respondAiMultiplayerInvite(batchId, outcome);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const setMultiplayerInvitePreference = createAsyncThunk(
  'skill-builder/set-multiplayer-invite-preference',
  async ({ muted }: { muted: boolean }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSetMultiplayerInvitePreference({
        multiplayerInvitePreferenceBody: { muted },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerReadyUsers = createAsyncThunk(
  'skill-builder/get-multiplayer-ready-users',
  async (
    {
      gameId,
      roomId,
    }: {
      gameId: string;
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetMultiplayerReadyUsers({ gameId, roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const fetchReady = async (gameId: string, roomId: string) =>
  (await apiClient.getQuestionApi()).questionApiMultiplayerReadyForNextSession({
    gameId,
    roomId,
  });

/**
 * Retried inside the thunk, not at its callers: every place that readies a
 * player (the lobby's auto-ready, a rematch, a late join) meets the same gap
 * between a round being created and its questions being dealt, which the
 * server answers with a "retry" rather than an error.
 */
const requestReady = async (
  gameId: string,
  roomId: string,
  attempt = 1,
): Promise<Awaited<ReturnType<typeof fetchReady>>> => {
  const response = await fetchReady(gameId, roomId);
  if (response.data?.msg !== ROUND_STARTING_RETRY) return response;
  if (attempt >= ROUND_STARTING_RETRY_ATTEMPTS) {
    arenaToast(ROUND_STARTING_GAVE_UP_MESSAGE, { tone: 'error' });
    return response;
  }
  if (attempt === 1) arenaToast(ROUND_STARTING_MESSAGE, { tone: 'info' });
  await wait(ROUND_STARTING_RETRY_DELAY_MS);
  return requestReady(gameId, roomId, attempt + 1);
};

export const readyForNextSession = createAsyncThunk(
  'skill-builder/ready-for-next-session',
  async (
    { gameId, roomId }: { gameId: string; roomId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await requestReady(gameId, roomId);
      // Still dealing after every retry: not ready, so callers must not mark
      // the player as if they were. Already told as info, so no snackbar.
      if (response.data?.msg === ROUND_STARTING_RETRY) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const removeOrBlockMultiplayerUser = createAsyncThunk(
  'skill-builder/remove-or-block-multiplayer-user',
  async (
    {
      roomId,
      payload,
    }: {
      roomId: string;
      payload: MultiplayerRemoveOrBlockUserBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiRemoveOrBlockMultiplayerUser({
        roomId,
        multiplayerRemoveOrBlockUserBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const leaveMultiplayerRoom = createAsyncThunk(
  'skill-builder/leave-multiplayer-room',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiLeaveMultiplayerRoom({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const createRequestForHostChange = createAsyncThunk(
  'skill-builder/create-request-for-host-change',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiRequestHostChange({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const acceptOrRejectHostChangeRequest = createAsyncThunk(
  'skill-builder/accept-or-reject-host-change-request',
  async (
    {
      roomId,
      payload,
    }: {
      roomId: string;
      payload: HostTransferBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiHandleHostTransferRequest({
        roomId,
        hostTransferBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const handleInactiveHost = createAsyncThunk(
  'skill-builder/handle-inactive-host',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiHandleInactiveHostTransfer({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminSkillTree = createAsyncThunk(
  'get/skill-builder/admin-skill-tree',
  async (
    {
      skillId,
      includeNoQuestionsSkills,
      showDuplicate,
      duplicateCutoff,
    }: {
      skillId: string;
      includeNoQuestionsSkills?: boolean;
      showDuplicate?: boolean;
      duplicateCutoff?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetAdminSkillTree({
        skillId,
        duplicateCutoff,
        includeNoQuestionsSkills,
        showDuplicate,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateMultiplayerPrompt = createAsyncThunk(
  'skill-builder/update-multiplayer-prompt',
  async (
    {
      roomId,
      multiplayerPromptUpdateBody,
    }: {
      roomId: string;
      multiplayerPromptUpdateBody: MultiplayerPromptUpdateBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiUpdateMultiplayerPromptText({
        roomId,
        multiplayerPromptUpdateBody,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const generateCustomPromptQuestions = createAsyncThunk(
  'skill-builder/generate-custom-prompt-questions',
  async (
    {
      gameId,
      roomId,
      multiplayerQuestionsGenerationBody,
    }: {
      gameId: string;
      roomId: string;
      multiplayerQuestionsGenerationBody: MultiplayerQuestionsGenerationBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiPromptBasedQuestionsGeneration({
        gameId,
        roomId,
        multiplayerQuestionsGenerationBody,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const nudgeMultiplayerUser = createAsyncThunk(
  'skill-builder/nudge-multiplayer-user',
  async (
    {
      gameId,
      roomId,
      targetUserId,
    }: {
      gameId: string;
      roomId: string;
      targetUserId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiNudgeMulplayerUser({
        gameId,
        roomId,
        multiplayerNudgeUserBody: { target_user_id: targetUserId },
      });
      return response.data;
    } catch (err: any) {
      arenaToast(nudgeErrorMessage(err?.response?.data?.error), {
        tone: 'info',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

/**
 * Relay an in-match reaction to the rest of the game. Fire and forget: a
 * refused or failed one (cooldown, a game that just ended) is not worth a toast
 * mid-question, and the sender has already seen their own.
 */
export const sendMultiplayerReaction = createAsyncThunk(
  'skill-builder/send-multiplayer-reaction',
  async (
    {
      roomId,
      gameId,
      reaction,
      targetUserIds,
    }: {
      roomId: string;
      gameId: string;
      reaction: MultiplayerReactionBodyReactionEnum;
      /** Empty or absent sends to everyone in the game. */
      targetUserIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiSendMultiplayerReaction({
        roomId,
        gameId,
        multiplayerReactionBody: {
          reaction,
          target_user_ids: targetUserIds?.length ? targetUserIds : null,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * The same nudge without a game to belong to — a player asking the host to run
 * the next match from the end-of-match screen, where no game exists yet.
 */
export const nudgeMultiplayerRoomUser = createAsyncThunk(
  'skill-builder/nudge-multiplayer-room-user',
  async (
    { roomId, targetUserId }: { roomId: string; targetUserId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiNudgeMulplayerUserInRoom({
        roomId,
        multiplayerNudgeUserBody: { target_user_id: targetUserId },
      });
      return response.data;
    } catch (err: any) {
      arenaToast(nudgeErrorMessage(err?.response?.data?.error), {
        tone: 'info',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateRoomName = createAsyncThunk(
  'skill-builder/update-room-name',
  async (
    {
      roomId,
      roomNameUpdateBody,
    }: {
      roomId: string;
      roomNameUpdateBody: MultiplayerRoomNameUpdateBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiUpdateMultiplayerRoomName({
        roomId,
        multiplayerRoomNameUpdateBody: roomNameUpdateBody,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMultiplayerGameInfo = createAsyncThunk(
  'skill-builder/get-multiplayer-game-info',
  async (
    {
      gameId,
      roomId,
    }: {
      gameId: string;
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetMultiplayerGameInfo({ gameId, roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const initializeMultiplayerGame = createAsyncThunk(
  'skill-builder/initialize-multiplayer-game',
  async (
    {
      roomId,
      data,
    }: {
      roomId: string;
      data: MultiplayerGameInitializeBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiInitializeMultiplayerGame({
        roomId,
        multiplayerGameInitializeBody: data,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJoinedMultiplayerRooms = createAsyncThunk(
  'skill-builder/get/joined-multiplayer-rooms',
  async (
    { page, pageSize }: { page?: number; pageSize?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetJoinedMultiplayerRooms({ page, pageSize });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getOverallRoomLeaderboard = createAsyncThunk(
  'skill-builder/get/overall-room-leaderboard',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiMultiplayerRoomLeaderboard({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getRoomGamesHistory = createAsyncThunk(
  'skill-builder/get/room-games-history',
  async (
    {
      roomId,
    }: {
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetMultiplayerGameHistory({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const checkAndGenerateQuestions = createAsyncThunk(
  'skill-builder/check-and-generate-questions',
  async (
    {
      gameId,
      roomId,
    }: {
      gameId: string;
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiCheckAndGeneratePromptQuestionsAvailability({
        gameId,
        roomId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const requestEndMultiplayerGame = createAsyncThunk(
  'skill-builder/request-end-game',
  async (
    {
      gameId,
      roomId,
    }: {
      gameId: string;
      roomId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiRequestMultiplayerGameEnd({ gameId, roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const rejectEndMultiplayerGameRequest = createAsyncThunk(
  'skill-builder/reject-end-game-request',
  async (
    {
      roomId,
      payload,
    }: {
      roomId: string;
      payload: HostTransferBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiHandleMultiplayerGameEnd({
        roomId,
        hostTransferBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const handleInactiveGame = createAsyncThunk(
  'skill-builder/handle-inactive-game',
  async (
    { roomId, gameId }: { gameId: string; roomId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiHandleInactiveGame({ gameId, roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const endActiveMultiplayerGame = createAsyncThunk(
  'skill-builder/end-active-multiplayer-game',
  async (
    { roomId, gameId }: { gameId: string; roomId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiEndMultiplayerGameByHost({ gameId, roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getBlockedRoomUsers = createAsyncThunk(
  'skill-builder/get-blocked-room-users',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetBlockedRoomUsers({ roomId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const unblockMultiplayerUser = createAsyncThunk(
  'skill-builder/unblock-multiplayer-user',
  async (
    { roomId, body }: { roomId: string; body: UnblockMultiplayerUserBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiUnblockMultiplayerUser({
        roomId,
        unblockMultiplayerUserBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const transferRoomAdmin = createAsyncThunk(
  'skill-builder/transfer-room-admin',
  async (
    { roomId, body }: { roomId: string; body: TransferRoomAdminBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiTransferRoomAdmin({
        roomId,
        transferRoomAdminBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteMultiplayerRoom = createAsyncThunk(
  'skill-builder/delete-multiplayer-room',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiDeleteMultiplayerRoom({
        exitMultiplayerRoomBody: { room_id: roomId },
      });
      const { data } = response;
      if (data.success === false) {
        enqueueSnackbar(getFormattedErr(data.msg));
        return rejectWithValue(data);
      }
      return data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const exitMultiplayerRoom = createAsyncThunk(
  'skill-builder/exit-multiplayer-room',
  async ({ roomId }: { roomId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiExitMultiplayerRoom({
        exitMultiplayerRoomBody: { room_id: roomId },
      });
      const { data } = response;
      if (data.success === false) {
        enqueueSnackbar(getFormattedErr(data.msg));
        return rejectWithValue(data);
      }
      return data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getPastPrompts = createAsyncThunk(
  'skill-builder/get-past-prompts',
  async ({ roomId }: { roomId?: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetPastPrompts({ roomId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getGuidedCourseList = createAsyncThunk(
  'skillBuilder/getGuidedCourseList',
  async (
    params: {
      query?: string;
      limit?: number;
      offset?: number;
      signal?: AbortSignal;
      /** For optional surfaces that simply hide on failure — no error toast. */
      silent?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCourses(
        {
          limit: params.limit,
          offset: params.offset,
          q: params.query,
        },
        {
          signal: params.signal,
        },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      if (!params.silent) {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * "Top N%" among a skill's candidates on Abekus — the caller's own, or a
 * public profile's (then only inside the top half). Silent on failure: every
 * surface that shows it simply leaves it out.
 */
export const getSkillPercentile = createAsyncThunk(
  'skillBuilder/getSkillPercentile',
  async (
    params: { skillId: string; username?: string; signal?: AbortSignal },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillPercentile(
        { skillId: params.skillId, username: params.username },
        { signal: params.signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const generatePromptCurriculum = createAsyncThunk(
  'skill-builder/generate-prompt-curriculum',
  async (
    { promptText, roomId }: { promptText: string; roomId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGeneratePromptCurriculum({
        roomId,
        generatePromptCurriculumBody: { prompt_text: promptText },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseInfo = createAsyncThunk(
  'skillBuilder/getCourseInfo',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCourseInfo({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseMap = createAsyncThunk(
  'skillBuilder/getCourseMap',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCourseMap({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseProgress = createAsyncThunk(
  'skillBuilder/getCourseProgress',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCourseProgress({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const purchaseGuidedCourse = createAsyncThunk(
  'skillBuilder/purchaseGuidedCourse',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiPurchaseGuidedCourse({ skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ─── Course review (public testimonial) ─────────────────────────────────────

export const submitCourseReview = createAsyncThunk(
  'skillBuilder/submitCourseReview',
  async (
    {
      skillId,
      rating,
      body,
      isPublic,
    }: {
      skillId: string;
      rating: number;
      body?: string;
      isPublic: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiSubmitCourseReview({
        skillId,
        submitCourseReviewBody: {
          rating,
          body,
          is_public: isPublic,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteCourseReview = createAsyncThunk(
  'skillBuilder/deleteCourseReview',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiDeleteCourseReview({ skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Declining pushes the next prompt out by the backoff; neverAsk silences it.
export const dismissCourseReview = createAsyncThunk(
  'skillBuilder/dismissCourseReview',
  async (
    { skillId, neverAsk = false }: { skillId: string; neverAsk?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiDismissCourseReview({
        skillId,
        dismissCourseReviewBody: { never_ask: neverAsk },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const dismissProgressResetNotice = createAsyncThunk(
  'skillBuilder/dismissProgressResetNotice',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiDismissProgressResetNotice({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const submitCourseFeedback = createAsyncThunk(
  'skillBuilder/submitCourseFeedback',
  async (
    {
      skillId,
      scope,
      targetId,
      rating,
      tags,
      body,
      sessionId,
    }: {
      skillId: string;
      scope: CourseFeedbackScope;
      targetId: string;
      rating: number;
      tags?: string[];
      body?: string;
      sessionId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiSubmitCourseFeedback({
        skillId,
        submitCourseFeedbackBody: {
          scope,
          target_id: targetId,
          rating,
          tags,
          body,
          session_id: sessionId,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * Publicly-listed series (learning paths) for the catalogue. Public and
 * user-independent, so it is safe to fetch alongside the course list.
 */
export const getPublicSeriesList = createAsyncThunk(
  'skillBuilder/getPublicSeriesList',
  async (params: { signal?: AbortSignal } | undefined, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetPublicSeriesList({ signal: params?.signal });
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * How well each catalogue course fits the signed-in learner, 0-100.
 *
 * Separate from the catalogue call on purpose: that one is cached 10 minutes
 * across every learner, so it cannot carry a per-user field. Comes back empty
 * for a learner with no skills and no stated role, which leaves /course in its
 * popularity order.
 */
export const getGuidedCourseRelevance = createAsyncThunk(
  'skillBuilder/getGuidedCourseRelevance',
  async (params: { signal?: AbortSignal } | undefined, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCourseRelevance({ signal: params?.signal });
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getGuidedCoursesProgress = createAsyncThunk(
  'skillBuilder/getGuidedCoursesProgress',
  async (params: { signal?: AbortSignal } | undefined, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCoursesProgress({ signal: params?.signal });
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * Everything the Skill Builder hero renders — streak, XP, certificates,
 * in-progress count, the week strip, and the course worth resuming.
 *
 * One call rather than composing the progress and login-streak endpoints on
 * the client: the hero is above the fold on every visit, and the week strip
 * needs practice days, which only the server can derive.
 */
export const getSkillDashboardSummary = createAsyncThunk(
  'skillBuilder/getSkillDashboardSummary',
  async (params: { signal?: AbortSignal } | undefined, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillDashboardSummary({ signal: params?.signal });
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const enrollGuidedCourse = createAsyncThunk(
  'skillBuilder/enrollGuidedCourse',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiEnrollGuidedCourse({ skillId });
      return response.data;
    } catch (err: any) {
      // Enrolling is what the "Start Learning" CTA does first, so a silent
      // rejection here reads as a dead button.
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getGuidedCourseCertificate = createAsyncThunk(
  'skillBuilder/getGuidedCourseCertificate',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCourseCertificate({ skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const generateGuidedCourseCertificate = createAsyncThunk(
  'skillBuilder/generateGuidedCourseCertificate',
  async (skillId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGenerateGuidedCourseCertificate({ skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const downloadGuidedCourseCertificate = createAsyncThunk(
  'skillBuilder/downloadGuidedCourseCertificate',
  async (
    { skillId, courseName }: { skillId: string; courseName?: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiDownloadGuidedCourseCertificate(
        { skillId },
        {
          responseType: 'blob',
          signal,
        },
      );

      const blob = new Blob([response.data as any], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName =
        courseName?.toLowerCase()?.replace(/[^a-z0-9_-]/gi, '_') || 'course';
      link.setAttribute('download', `${safeName}_certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);

      return { status: 'success' };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const completeGuidedLabel = createAsyncThunk(
  'skillBuilder/completeGuidedLabel',
  async (
    { skillId, labelId }: { skillId: string; labelId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiCompleteGuidedLabel({ labelId, skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getLabelContent = createAsyncThunk(
  'skillBuilder/getLabelContent',
  async (
    { skillId, labelId }: { skillId: string; labelId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetLabelContent({ labelId, skillId, supportsBuilding: true });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getIntroContent = createAsyncThunk(
  'skillBuilder/getIntroContent',
  async (
    {
      skillId,
      nodeId,
      forceWelcome = false,
    }: { skillId: string; nodeId: string; forceWelcome?: boolean },
    { rejectWithValue },
  ) => {
    try {
      return (await apiClient.getIntroContent(
        skillId,
        nodeId,
        forceWelcome,
      )) as IntroContent;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * Stop an intro auto-opening. Fire-and-forget from the learner's point of view:
 * a failure here must not block them walking on into the lesson, so it does not
 * surface a snackbar.
 */
export const completeIntro = createAsyncThunk(
  'skillBuilder/completeIntro',
  async (
    { skillId, nodeId }: { skillId: string; nodeId: string },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.completeIntro(skillId, nodeId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getLessonBuildStatus = createAsyncThunk(
  'skillBuilder/getLessonBuildStatus',
  async (
    {
      skillId,
      labelId,
      force = false,
    }: { skillId: string; labelId: string; force?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetLabelBuildStatus({ labelId, skillId, force });
      return response.data;
    } catch (err: any) {
      // Deliberately silent: this is polled on a timer behind a loader that is
      // already telling the learner what is happening. A blip between polls is
      // not news, and a snackbar per tick would be.
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const theoryAiChat = createAsyncThunk(
  'skillBuilder/theoryAiChat',
  async (
    {
      skillId,
      labelSkillId,
      userQuery,
      cardIndex,
      parentResponseId,
    }: {
      skillId: string;
      labelSkillId: string;
      userQuery: string;
      cardIndex?: number;
      parentResponseId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiTheoryAiChat({
        skillId,
        theoryAIChatBody: {
          label_skill_id: labelSkillId,
          user_query: userQuery,
          card_index: cardIndex,
          parent_response_id: parentResponseId,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * No entry for no_questions_found: this thunk sends supportsBuilding, so a
 * lesson with an empty pool comes back as a 200 the caller renders as "building"
 * rather than reaching here at all.
 */
const GUIDED_SESSION_ERROR_COPY: Record<string, string> = {
  not_enrolled: 'Open the lesson first, then start the quiz.',
};

export const startGuidedLabelSession = createAsyncThunk(
  'skillBuilder/startGuidedLabelSession',
  async (
    {
      skillId,
      labelId,
      forceNew = false,
    }: { skillId: string; labelId: string; forceNew?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiStartGuidedLabelSession({
        labelId,
        skillId,
        // This client renders a lesson that is still being written. Without the
        // flag the server keeps its old 400, which is what old app builds need.
        supportsBuilding: true,
        startGuidedLabelSessionBody: {
          force_new: forceNew,
        },
      });
      return response.data;
    } catch (err: any) {
      // This thunk used to swallow the error entirely — the Start Quiz button
      // spun, reset, and said nothing, which is how a course whose lesson had no
      // questions looked like a dead button. A lesson still being generated is
      // now a 200 the caller handles, so anything reaching here is a real
      // failure and the learner should hear about it.
      enqueueSnackbar(getApiErrorMessage(err, GUIDED_SESSION_ERROR_COPY));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getGuidedSessionProgress = createAsyncThunk(
  'skillBuilder/getGuidedSessionProgress',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedSessionProgress({ sessionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getGuidedCurrentQuestion = createAsyncThunk(
  'skillBuilder/getGuidedCurrentQuestion',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetGuidedCurrentQuestion({ sessionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const attemptGuidedQuestion = createAsyncThunk(
  'skillBuilder/attemptGuidedQuestion',
  async (
    {
      sessionId,
      questionId,
      payload,
    }: {
      sessionId: string;
      questionId: string;
      payload: import('src/openapi').AttemptQuestionBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAttemptGuidedQuestion({
        questionId,
        sessionId,
        attemptQuestionBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getTheoryAiChats = createAsyncThunk(
  'skillBuilder/getTheoryAiChats',
  async (
    { skillId, cardIndex }: { skillId: string; cardIndex?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetTheoryAiChats({ skillId, cardIndex });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const startGuidedTopicSession = createAsyncThunk(
  'skillBuilder/startGuidedTopicSession',
  async (
    { skillId, topicId }: { skillId: string; topicId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiStartGuidedTopicSession({ skillId, topicId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getTopicTestProgress = createAsyncThunk(
  'skillBuilder/getTopicTestProgress',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetTopicTestProgress({ sessionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const closeTopicTestSession = createAsyncThunk(
  'skillBuilder/closeTopicTestSession',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiCloseTopicTestSession({ sessionId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const startTopicJumpSession = createAsyncThunk(
  'skillBuilder/startTopicJumpSession',
  async (
    { skillId, topicId }: { skillId: string; topicId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiStartTopicJump({ skillId, topicId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const closeTopicJumpSession = createAsyncThunk(
  'skillBuilder/closeTopicJumpSession',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiCloseTopicJump({ sessionId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getTopicTestResult = createAsyncThunk(
  'skillBuilder/getTopicTestResult',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetTopicTestResult({ sessionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getTopicJumpResult = createAsyncThunk(
  'skillBuilder/getTopicJumpResult',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetTopicJumpResult({ sessionId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getTopicTestCurrentQuestion = createAsyncThunk(
  'skillBuilder/getTopicTestCurrentQuestion',
  async (
    { sessionId, questionId }: { sessionId: string; questionId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetTopicTestCurrentQuestion({ sessionId, questionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const attemptTopicTestQuestion = createAsyncThunk(
  'skillBuilder/attemptTopicTestQuestion',
  async (
    {
      sessionId,
      questionId,
      payload,
    }: {
      sessionId: string;
      questionId: string;
      payload: {
        question_type?: string;
        question_skipped?: boolean;
        timeout?: boolean;
        answer_option_no?: number[];
        answer_one_word?: string;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAttemptTopicTestQuestion({
        questionId,
        sessionId,
        attemptQuestionBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);
